import { Outlet, Link, useLocation } from "react-router-dom";
import { TAB_REGISTRY, appPath, type TabConfig, type AppConfig } from "@/registry/appRegistry";
import { useUserPermissions } from "@/api/hooks/permissions";
import {
  AppBar,
  Box,
  Button,
  IconButton,
  Toolbar,
  Typography,
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  Business,
  Healing,
  History,
  AssignmentLate,
  Apps,
  Logout,
  Person,
} from "@mui/icons-material";
import { useState } from "react";
import { useAuth } from "@/auth/AuthContext";

const SIDEBAR_WIDTH = 224;
const SIDEBAR_COLLAPSED = 56;
const TOPBAR_HEIGHT = 50;

const ICON_MAP: Record<string, React.ReactNode> = {
  Building2: <Business fontSize="small" />,
  Stethoscope: <Healing fontSize="small" />,
  Worklist: <AssignmentLate fontSize="small" />,
  History: <History fontSize="small" />,
  Person: <Person fontSize="small" />,
};

function findActive(pathname: string): { tab: TabConfig | undefined; app: AppConfig | undefined } {
  const tab = TAB_REGISTRY.find((t) => pathname.startsWith(t.basePath));
  if (!tab) return { tab: undefined, app: undefined };
  const rest = pathname.slice(tab.basePath.length).replace(/^\//, "");
  const app = tab.apps.find((a) =>
    a.basePath ? rest === a.basePath || rest.startsWith(a.basePath + "/") : rest === "",
  );
  return { tab, app };
}

export default function AppShell() {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const { user, logout } = useAuth();
  const { data: permissions, isError: permissionsError } = useUserPermissions();
  const allowedTabs = permissions?.tabs ?? [];
  const isClinopsAdmin = permissions?.is_clinops_admin ?? false;
  const width = collapsed ? SIDEBAR_COLLAPSED : SIDEBAR_WIDTH;

  const visibleTabs = TAB_REGISTRY.filter((t) => allowedTabs.includes(t.id));
  const { tab: activeTab, app: activeApp } = findActive(location.pathname);

  const relativePath = activeTab && activeApp
    ? location.pathname.slice(`${activeTab.basePath}/${activeApp.basePath}`.length).replace(/^\//, "")
    : "";

  const isAppNavActive = (itemPath: string) => {
    if (itemPath === "" && relativePath === "") return true;
    if (itemPath === "") return false;
    return relativePath === itemPath || relativePath.startsWith(itemPath + "/");
  };

  return (
    // Flex-row root: sidebar runs the full viewport height, the
    // top bar lives inside the main column to its right, so the
    // two regions visually split cleanly with no shared seam.
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "background.default" }}>
      {/* Sidebar — sticky-pinned to the top, full-viewport height. */}
      <Box
        component="aside"
        sx={{
          width,
          flexShrink: 0,
          position: "sticky",
          top: 0,
          alignSelf: "flex-start",
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          transition: "width 0.2s",
          overflow: "hidden",
          bgcolor: "primary.main",
        }}
      >
        {/* Logo + collapse toggle. Logo lives at top-left of the
         * sidebar so the top bar (right side) doesn't carry brand
         * art. */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 1,
            height: TOPBAR_HEIGHT,
            flexShrink: 0,
            justifyContent: collapsed ? "center" : "space-between",
          }}
        >
          {!collapsed && (
            <Link
              to="/"
              style={{ textDecoration: "none", display: "flex", alignItems: "center" }}
              aria-label="Pedestal Health home"
            >
              {/* Brand art is dark on transparent, so we sit it on
               * a white chip to keep contrast against Azure. */}
              <Box
                sx={{
                  bgcolor: "common.white",
                  px: 1,
                  py: 0.5,
                  borderRadius: 0.75,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Box
                  component="img"
                  src="/images/pedestal-health-logo.png"
                  alt="Pedestal Health"
                  sx={{ display: "block", height: 24, width: "auto" }}
                />
              </Box>
            </Link>
          )}
          <IconButton
            size="small"
            onClick={() => setCollapsed(!collapsed)}
            sx={{ color: "primary.contrastText" }}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed ? <ChevronRight /> : <ChevronLeft />}
          </IconButton>
        </Box>

        {/* Apps within the active tab */}
        <Box sx={{ flex: 1, py: 1.5, px: 1, overflowY: "auto" }}>
          {activeTab?.apps
            .filter((app) => !app.adminOnly || isClinopsAdmin)
            .map((app) => {
            const isActive = activeApp?.id === app.id;
            return (
              <Link key={app.id} to={appPath(activeTab, app)} style={{ textDecoration: "none" }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    px: 1.5,
                    py: 1,
                    borderRadius: 1.5,
                    mb: 0.5,
                    color: isActive ? "primary.contrastText" : "rgba(255,255,255,0.7)",
                    bgcolor: isActive ? "rgba(255,255,255,0.25)" : "transparent",
                    "&:hover": { bgcolor: "rgba(255,255,255,0.15)", color: "primary.contrastText" },
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    transition: "all 0.15s",
                  }}
                >
                  {ICON_MAP[app.icon] ?? <Apps fontSize="small" />}
                  {!collapsed && app.name}
                </Box>
              </Link>
            );
          })}
        </Box>

        {/* User + logout — deeper Navy band so it reads as a quiet
         * footer against the Azure sidebar. */}
        <Box
          sx={{
            p: 1,
            borderTop: "0px solid rgba(255,255,255,0.15)",
            bgcolor: "primary.main",
          }}
        >
          {!collapsed && user && (
            <Typography
              variant="caption"
              sx={{
                display: "block",
                px: 1.5,
                pb: 0.5,
                color: "rgba(255,255,255,0.7)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={user.email}
            >
              {user.email || user.username}
            </Typography>
          )}
          <Box
            onClick={logout}
            role="button"
            aria-label="Log out"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              px: 1.5,
              py: 1,
              borderRadius: 1.5,
              cursor: "pointer",
              color: "rgba(255,255,255,0.7)",
              "&:hover": { bgcolor: "rgba(255,255,255,0.15)", color: "primary.contrastText" },
              fontSize: "0.875rem",
            }}
          >
            <Logout fontSize="small" />
            {!collapsed && "Log out"}
          </Box>
        </Box>
      </Box>

      {/* Main column: top bar + sub-nav + page content. */}
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
        {/* Top bar (Pearl) — only spans the main column so its left
         * edge sits flush against the sidebar's right edge. */}
        <AppBar
          position="sticky"
          elevation={0}
          sx={{
            bgcolor: "background.default",
            color: "text.primary",
            top: 0,
            zIndex: (theme) => theme.zIndex.appBar,
          }}
        >
          <Toolbar sx={{ minHeight: `${TOPBAR_HEIGHT}px !important`, gap: 1 }}>
            {permissionsError ? (
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", fontStyle: "italic" }}
              >
                Authorization service unavailable
              </Typography>
            ) : (
              visibleTabs.map((tab) => {
                const active = activeTab?.id === tab.id;
                return (
                  <Link key={tab.id} to={tab.basePath} style={{ textDecoration: "none" }}>
                    <Button
                      size="small"
                      variant={active ? "contained" : "text"}
                      color="primary"
                      sx={{
                        boxShadow: "none",
                      }}
                    >
                      {tab.label}
                    </Button>
                  </Link>
                );
              })
            )}
            <Box sx={{ flex: 1 }} />
          </Toolbar>
        </AppBar>

        {/* App sub-nav band */}
        {activeApp && activeApp.navItems.length > 0 && (
          <Box
            sx={{
              bgcolor: "background.paper",
              borderBottom: "1px solid",
              borderColor: "divider",
              px: 3,
              py: 1,
              display: "flex",
              gap: 1,
              alignItems: "center",
            }}
          >
            {activeApp.navItems.map((item) => {
              const active = isAppNavActive(item.path);
              const fullPath = `${activeTab!.basePath}/${activeApp.basePath}${item.path ? "/" + item.path : ""}`;
              return (
                <Link key={item.path} to={fullPath} style={{ textDecoration: "none" }}>
                  <Button
                    size="small"
                    variant={active ? "contained" : "text"}
                    sx={{ boxShadow: "none" }}
                  >
                    {item.label}
                  </Button>
                </Link>
              );
            })}
          </Box>
        )}

        {/* Page content */}
        <Box component="main" sx={{ flex: 1, px: { xs: 2, sm: 3, lg: 4 }, py: 3, maxWidth: 1280, mx: "auto", width: "100%" }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}
